<template>
  <InfuseTable
    :columns="columns"
    :rows="rowsWithReadonly"
    :active="(row: Role, id?: number) => active(row, id)"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :disabled-rows="disabledRows"
    :removable="removable"
    :editable="editable"
    @selected="(value: Role[]) => (selectedRows = value)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="(row: Role) => emit('clickRow', row)"
    @remove="(row: Role) => emit('remove', row)"
    @edit="(row: Role) => emit('edit', row)"
    :remove-permission-id="rolePermissions.DELETE"
    :edit-permission-id="rolePermissions.UPDATE"
  >
    <template #actions>
      <InfuseButton
        class="flex w-64 items-center"
        size="medium"
        @click="emit('create')"
        v-permission="{ id: rolePermissions.CREATE, hide: false }"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.roles.header.add_button') }}
      </InfuseButton>
    </template>

    <template #level="{ row }">
      <div class="flex flex-row gap-2">
        <RoleLevelTag :role="row" />
      </div>
    </template>

    <template #ranking="{ data, row }">
      <div class="flex flex-row gap-4">
        <div class="flex flex-row items-center gap-2">
          <font-awesome-icon :icon="getRankingIcon(data)" class="text-neutral-600" />
          <span v-if="editingRankingRowId !== row.id">{{ data }}</span>
          <input
            v-else
            ref="rankingInputRef"
            type="number"
            :value="editingRankingValue"
            @input="(e) => (editingRankingValue = Number((e.target as HTMLInputElement).value))"
            @keyup.enter="setRanking(row)"
            @blur="cancelEditRanking"
            @click.stop
            class="w-16 rounded border border-neutral-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
            :disabled="loadingRanking"
            min="1"
          />
        </div>
        <div class="flex flex-row invisible group-hover:visible" v-if="!row.readonly">
          <font-awesome-icon
            v-if="!loadingRanking && editingRankingRowId !== row.id"
            :icon="['far', 'pen']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="startEditRanking(row, data)"
            v-permission="{
              ids: [
                rolePermissions.SET_RANKING,
                tenantRolePermissions.SET_RANKING,
                organisationRolePermissions.SET_RANKING
              ],
              hide: false
            }"
          />
          <font-awesome-icon
            v-if="!loadingRanking && editingRankingRowId !== row.id"
            :icon="['far', 'arrow-down']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="emit('demote', row)"
            v-permission="{
              ids: [rolePermissions.DEMOTE, tenantRolePermissions.DEMOTE, organisationRolePermissions.DEMOTE],
              hide: false
            }"
          />
          <font-awesome-icon
            v-if="!loadingRanking && data > 1 && editingRankingRowId !== row.id"
            :icon="['far', 'arrow-up']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="emit('promote', row)"
            v-permission="{
              ids: [rolePermissions.PROMOTE, tenantRolePermissions.PROMOTE, organisationRolePermissions.PROMOTE],
              hide: false
            }"
          />

          <font-awesome-icon v-if="loadingRanking" :icon="['far', 'spinner']" class="px-5" spin-pulse />
        </div>
      </div>
    </template>

    <template #createdAt="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #modifiedAt="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #scope="{ row }">
      <InfuseTooltip :content="getScopeTooltip(row, t)" offset-distance="4">
        <InfuseTag :inline="true">
          {{ getScopeText(row, t) }}
        </InfuseTag>
      </InfuseTooltip>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { rolePermissions } from '@infuse/api/iam/role';
import { vPermission } from '@infuse/iam/permission/directives';
import { RoleLevelTag } from '@infuse/iam/role/ui';
import { Role } from '@infuse/iam/role/type';
import { getRankingIcon, getScopeText, getScopeTooltip, isRoleSharedToThisLevel, RoleLevel } from './roleScope';
import { InfuseButton, InfuseTable, InfuseTag, InfuseTooltip } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { formatDate } from '@infuse/ui/utils';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { tenantRolePermissions } from '@infuse/api/iam/tenant';
import { organisationRolePermissions } from '@infuse/api/iam/organisation';

const { t } = useI18n();

interface Emits {
  (e: 'load'): void;

  (e: 'clickRow', row: Role): void;

  (e: 'search', query: string): void;

  (e: 'remove', row: Role): void;

  (e: 'edit', row: Role): void;

  (e: 'promote', row: Role): void;

  (e: 'demote', row: Role): void;

  (e: 'set-ranking', row: Role, ranking: number): void;

  (e: 'create'): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<Role[]>([]);
const editingRankingRowId = ref<string | null>(null);
const editingRankingValue = ref<number>(0);
const rankingInputRef = ref<HTMLInputElement | null>(null);

interface Props {
  rows: Role[];
  active?: (row: Role, id?: number) => boolean;
  loading: boolean;
  loadingRanking?: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  removable?: boolean;
  editable?: boolean;
  level?: RoleLevel;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  active: () => false,
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  removable: false,
  editable: false,
  hasMoreItems: true
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.roles.search_placeholder'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => {
  const row = selectedRows.value[0];
  if (!row) {
    return [];
  }

  return props.rows.filter((user) => user.id !== row.id);
});

const rowsWithReadonly = computed(() => {
  return props.rows.map((row) => {
    return {
      ...row,
      readonly: isRoleSharedToThisLevel(row, props.level)
    };
  });
});

function startEditRanking(row: Role, currentRanking: number): void {
  editingRankingRowId.value = row.id;
  editingRankingValue.value = currentRanking;
  nextTick(() => {
    rankingInputRef.value?.focus();
    rankingInputRef.value?.select();
  });
}

function setRanking(row: Role): void {
  if (editingRankingValue.value > 0) {
    emit('set-ranking', row, editingRankingValue.value);
    editingRankingRowId.value = null;
  }
}

function cancelEditRanking(): void {
  editingRankingRowId.value = null;
}

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('iam.roles.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'description',
    label: () => t('iam.roles.description'),
    field: 'description'
  },
  {
    name: 'level',
    label: () => t('iam.roles.level'),
    field: 'level'
  },
  {
    name: 'scope',
    label: () => t('iam.roles.scope'),
    field: 'scope'
  },
  {
    name: 'userCount',
    label: () => t('iam.roles.userCount'),
    field: 'userCount'
  },
  {
    name: 'ranking',
    label: () => t('iam.roles.ranking'),
    field: 'ranking'
  },
  {
    name: 'createdAt',
    label: () => t('iam.roles.createdAt'),
    field: 'createdAt'
  },
  {
    name: 'modifiedAt',
    label: () => t('iam.roles.modifiedAt'),
    field: 'modifiedAt'
  }
];
</script>
